import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { PapiDeviation } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import {
  BiData,
  DeviationEditClickBiEvent,
  ItemClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import useDuperbrowseLink from '@wix/da-shared-react/pkg/Duperbrowse/hooks/useDuperbrowseLink';
import { getDeviationEntityId } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { StudioSection } from '../types/store';
import { getActiveSection } from '../selectors/studio';
import { useEditDeviationClick } from './useEditDeviationClick';

export const VIEW_ACTION_SECTIONS = [
  StudioSection.STASH,
  StudioSection.PLAYGROUND,
  StudioSection.PRODUCTS_TIER,
  StudioSection.PRODUCTS_TIER_UNLISTED,
];

export type MainAction = 'view' | 'edit';

export interface Result {
  mainAction: MainAction;
  onClick: (e: React.MouseEvent<any>) => void;
}

/** This hook returns the main action to be performed when clicking a studio list or grid item */
export function useMainActionDeviationClick(
  targetDeviation: PapiDeviation,
  scrollToRef: React.RefObject<HTMLElement | null>,
  /** sectionname to pass to BI */
  sectionname?: string
): Result {
  const activeSection = useSelector(getActiveSection);

  const { onEditClick } = useEditDeviationClick(targetDeviation);
  const onViewDeviationClick = useDuperbrowseLink(
    getDeviationEntityId(targetDeviation),
    scrollToRef,
    undefined,
    false
  );

  const onViewTierClick = useCallback(
    (ev: React.MouseEvent<any>) => {
      if (targetDeviation.tier) {
        window.location.assign(Url.studioTier(targetDeviation.tier.tierId));
      }
    },
    [targetDeviation]
  );

  // todo: should prob be from a context set in the page
  const mainAction = VIEW_ACTION_SECTIONS.includes(activeSection)
    ? 'view'
    : 'edit';

  // wrap main action click handler with bi
  const logEvent = useBiLogger();
  const onClick = useCallback(
    (ev: React.MouseEvent<any>) => {
      const commonBiData = {
        itemid: targetDeviation.deviationId,
        typeid: getDeviationResourceType(targetDeviation),
        sectionname,
      };

      if (mainAction === 'view') {
        logEvent(BiData<ItemClickBiEvent>({ evid: 100, ...commonBiData }));
        switch (activeSection) {
          case StudioSection.PRODUCTS_TIER:
          case StudioSection.PRODUCTS_TIER_UNLISTED:
            onViewTierClick(ev);
            break;
          default:
            onViewDeviationClick(ev);
            break;
        }
      } else {
        // default to mainAction === 'edit'
        logEvent(
          BiData<DeviationEditClickBiEvent>({ evid: 371, ...commonBiData })
        );
        onEditClick();
      }
    },
    [
      targetDeviation,
      activeSection,
      sectionname,
      mainAction,
      logEvent,
      onViewDeviationClick,
      onViewTierClick,
      onEditClick,
    ]
  );

  return {
    mainAction,
    onClick,
  };
}

export function getSecondaryActionFromMain(main: MainAction): MainAction {
  return main === 'view' ? 'edit' : 'view';
}
